import React, { useState, useEffect, useCallback } from 'react';
import './Reviews.css';
import DashboardSidebar from './DashboardSidebar';
import { useNavigate } from "react-router-dom";
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { Star, MessageCircle, TrendingUp } from 'lucide-react';

// Local Storage Helper
const LocalStorageHelper = {
  saveLoginData: (userId, email, name, userType = 'user') => {
    localStorage.setItem('isLoggedIn', 'true');
    localStorage.setItem('userId', userId);
    localStorage.setItem('userEmail', email);
    localStorage.setItem('userName', name);
    localStorage.setItem('userType', userType);
  },

  isLoggedIn: () => {
    return localStorage.getItem('isLoggedIn') === 'true';
  },

  getLoginData: () => {
    return {
      userId: localStorage.getItem('userId'),
      email: localStorage.getItem('userEmail'),
      name: localStorage.getItem('userName'),
      userType: localStorage.getItem('userType'),
    };
  },

  clearLoginData: () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    localStorage.removeItem('userType');
  }
};

const Reviews = () => {
  const navigate = useNavigate();
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Authentication state
  const [currentUser, setCurrentUser] = useState(null);
  const [photographerData, setPhotographerData] = useState(null);
  const [photographerName, setPhotographerName] = useState('Photographer');
  const [profilePhoto, setProfilePhoto] = useState(null);

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [isLoadingReviews, setIsLoadingReviews] = useState(true);
  const [reviewStats, setReviewStats] = useState({
    totalReviews: 0,
    averageRating: 0,
    recentReviews: 0,
    ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
  });

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile && !sidebarVisible) {
        setSidebarVisible(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarVisible]);

  // Authentication check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        await loadPhotographerData(user.uid);
        await loadProfilePhoto(user.uid);
        await loadReviews(user.uid);
      } else {
        navigate('/Login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load photographer data
  const loadPhotographerData = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'photographers', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setPhotographerData(userData);
        setPhotographerName(userData.name || userData.displayName || 'Photographer');
      } else {
        if (auth.currentUser) {
          setPhotographerName(auth.currentUser.displayName || 'Photographer');
        }
      }
    } catch (error) {
      console.error('Error loading photographer data:', error);
      if (auth.currentUser) {
        setPhotographerName(auth.currentUser.displayName || 'Photographer');
      }
    }
  };

  // Load profile photo
  const loadProfilePhoto = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'photographers', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const profilePhotoUrl = userData.profileImage || null;
        setProfilePhoto(profilePhotoUrl);
      }
    } catch (error) {
      console.error('Error loading profile photo:', error);
    }
  };

  // UPDATED: Load reviews for the photographer from the new structure
  const loadReviews = useCallback(async (photographerId) => {
    try {
      setIsLoadingReviews(true);
      console.log('Loading reviews for photographer:', photographerId);
      
      // Updated to match your portfolio page structure: reviews/{photographerId}/reviews
      const reviewsQuery = query(
        collection(db, 'reviews', photographerId, 'reviews'),
        orderBy('createdAt', 'desc')
      );
      
      const reviewsSnapshot = await getDocs(reviewsQuery);
      const reviewsData = [];
      
      reviewsSnapshot.forEach((doc) => {
        const reviewData = doc.data();
        reviewsData.push({
          id: doc.id,
          ...reviewData,
          createdAt: reviewData.createdAt?.toDate() || new Date()
        });
      });
      
      console.log('Loaded reviews:', reviewsData);
      setReviews(reviewsData);
      calculateReviewStats(reviewsData);
      
    } catch (error) {
      console.error('Error loading reviews:', error);
      
      // Fallback: Try the old structure if new structure fails
      try {
        console.log('Trying fallback structure...');
        const fallbackQuery = query(
          collection(db, 'reviews'),
          where('photographerId', '==', photographerId),
          orderBy('createdAt', 'desc')
        );
        
        const fallbackSnapshot = await getDocs(fallbackQuery);
        const fallbackData = [];
        
        fallbackSnapshot.forEach((doc) => {
          const reviewData = doc.data();
          fallbackData.push({
            id: doc.id,
            ...reviewData,
            createdAt: reviewData.createdAt?.toDate() || new Date()
          });
        });
        
        console.log('Loaded reviews from fallback:', fallbackData);
        setReviews(fallbackData);
        calculateReviewStats(fallbackData);
        
      } catch (fallbackError) {
        console.error('Error with fallback query:', fallbackError);
        setReviews([]);
        calculateReviewStats([]);
      }
    } finally {
      setIsLoadingReviews(false);
    }
  }, []);

  // Calculate review statistics
  const calculateReviewStats = (reviewsData) => {
    const totalReviews = reviewsData.length;
    const averageRating = totalReviews > 0 
      ? reviewsData.reduce((sum, review) => sum + review.rating, 0) / totalReviews 
      : 0;
    
    // Recent reviews (last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recentReviews = reviewsData.filter(review => review.createdAt > thirtyDaysAgo).length;
    
    // Rating distribution
    const ratingDistribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviewsData.forEach(review => {
      ratingDistribution[review.rating]++;
    });

    setReviewStats({
      totalReviews,
      averageRating: parseFloat(averageRating.toFixed(1)),
      recentReviews,
      ratingDistribution
    });
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      LocalStorageHelper.clearLoginData();
      navigate('/Login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Handle home navigation
  const handleHomeNavigation = () => {
    navigate('/LandingPage');
    closeSidebar();
  };

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  const closeSidebar = () => {
    if (isMobile) {
      setSidebarVisible(false);
    }
  };

  // Format review date
  const formatReviewDate = (date) => {
    if (!date) return 'Recently';
    
    const now = new Date();
    const reviewDate = date instanceof Date ? date : new Date(date);
    const diffInHours = Math.floor((now - reviewDate) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInHours < 48) return 'Yesterday';
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)} days ago`;
    
    return reviewDate.toLocaleDateString();
  };

  // Render stars
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        size={15}
        className={`review-star ${index < rating ? 'filled' : ''}`}
        fill={index < rating ? '#e8a33d' : 'none'}
        color={index < rating ? '#e8a33d' : '#e4e4e7'}
      />
    ));
  };

  return (
    <div className="reviews-container">
      <DashboardSidebar
        activeItem="reviews"
        visible={sidebarVisible}
        isMobile={isMobile}
        onToggle={toggleSidebar}
        onClose={closeSidebar}
        userName={photographerName}
        profilePhoto={profilePhoto}
        onHome={handleHomeNavigation}
        onLogout={handleLogout}
      />

      {/* Main Content */}
      <main className={`reviews-main ${
        !sidebarVisible && !isMobile ? 'expanded' : ''
      }`}>
        <h1 className="reviews-header">Reviews & Ratings</h1>
        
        {/* Review Statistics */}
        <section className="reviews-stats-section">
          <h2 className="reviews-section-title">Review Overview</h2>
          <div className="reviews-stats-grid">
            <div className="reviews-stat-card">
              <div className="reviews-stat-icon">
                <MessageCircle size={24} />
              </div>
              <div className="reviews-stat-content">
                <h3>Total Reviews</h3>
                <div className="reviews-stat-value">{reviewStats.totalReviews}</div>
              </div>
            </div>
            <div className="reviews-stat-card">
              <div className="reviews-stat-icon">
                <Star size={24} />
              </div>
              <div className="reviews-stat-content">
                <h3>Average Rating</h3>
                <div className="reviews-stat-value">{reviewStats.averageRating}</div>
              </div>
            </div>
            <div className="reviews-stat-card">
              <div className="reviews-stat-icon">
                <TrendingUp size={24} />
              </div>
              <div className="reviews-stat-content">
                <h3>Recent Reviews</h3>
                <div className="reviews-stat-value">{reviewStats.recentReviews}</div>
              </div>
            </div>
          </div>
        </section>

        {/* Rating Distribution */}
        <section className="reviews-distribution-section">
          <h2 className="reviews-section-title">Rating Distribution</h2>
          <div className="reviews-distribution-chart">
            {[5, 4, 3, 2, 1].map(rating => (
              <div key={rating} className="reviews-distribution-row">
                <span className="reviews-distribution-label">{rating} stars</span>
                <div className="reviews-distribution-bar">
                  <div 
                    className="reviews-distribution-fill"
                    style={{ 
                      width: reviewStats.totalReviews > 0 
                        ? `${(reviewStats.ratingDistribution[rating] / reviewStats.totalReviews) * 100}%` 
                        : '0%' 
                    }}
                  ></div>
                </div>
                <span className="reviews-distribution-count">
                  {reviewStats.ratingDistribution[rating]}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* UPDATED: Recent Reviews with User Details */}
        <section className="reviews-list-section">
          <h2 className="reviews-section-title">Recent Reviews</h2>
          {isLoadingReviews ? (
            <div className="reviews-loading">
              <div className="reviews-spinner"></div>
              <p>Loading reviews...</p>
            </div>
          ) : reviews.length > 0 ? (
            <div className="reviews-list">
              {reviews.map((review) => (
                <div key={review.id} className="reviews-review-card">
                  <div className="reviews-review-header">
                    <div className="reviews-review-user">
                      <div className="reviews-review-avatar">
                        {review.userName ? review.userName.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div className="reviews-review-user-info">
                        <h4>{review.userName || 'Anonymous User'}</h4>
                        <span className="reviews-review-email">
                          {review.userEmail || 'No email provided'}
                        </span>
                        <span className="reviews-review-date">
                          {formatReviewDate(review.createdAt)}
                        </span>
                      </div>
                    </div>
                    <div className="reviews-review-rating">
                      {renderStars(review.rating)}
                      <span className="reviews-rating-number">({review.rating}/5)</span>
                    </div>
                  </div>
                  <div className="reviews-review-content">
                    <p>{review.reviewText}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="reviews-no-reviews">
              <MessageCircle size={48} />
              <h3>No reviews yet</h3>
              <p>You haven't received any reviews yet. Keep providing excellent service!</p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default Reviews;
